/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    array, bool, func, object, string,
} from 'prop-types';
import { uid } from 'react-uid';

// Material UI
import { Button, Grid, makeStyles } from '@material-ui/core';

import {
    addDays, endOfWeek, format, isSameMonth, isSameWeek, startOfWeek,
} from 'date-fns';

import { convertDateObjToStrDate, convertDateStringToObj, MonthsFullName } from '../../helpers/Calendar-Helper';
import { getIsPassportUserLoggedIn, getShopRunnerToken } from '../../../../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getPassportSubscriptionStatus } from '../../../../../../../../../state/ducks/MfeCheckout/ducks/Cart/Cart-Selectors';
import { calendarColorStyle } from '../../../Helpers/Style-Helper';
import { trackEvent } from '../../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles(
    (theme) => ({
        calendarContainer: {
            // columnCount: 2,
            padding: '0 41px 16px',
            minHeight: '282px',
            borderBottom: `1px solid ${calendarColorStyle.primary[100]}`,
            marginBottom: '16px',
            [theme.breakpoints.down(490)]: {
                padding: '0 16px 16px',
            },
        },
        monthCalendar: {
            // breakInside: 'avoid-page', // columns fluid
            // breakInside: 'avoid', // columns even
            marginBottom: '0 8px 8px',
        },
        weekdays: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)', /* 7 equal columns */
            gap: '1px', /* Optional: space between day cells */
            width: '100%',
        },
        weekContainer: {
            textAlign: 'center',
            padding: '0',
            backgroundColor: calendarColorStyle.common.default, /* Example background */
            fontSize: '0.875em',
        },
        daysGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)', /* 7 equal columns */
            gap: '0',
            width: '100%',
        },
        dayContainer: {
            display: 'block',
            textAlign: 'center',
            padding: '0',
            marginBottom: '4px',
            border: `1px solid ${calendarColorStyle.common.default}`,
            '& button': {
                fontSize: '0.875em',
                lineHeight: '1em',
                display: 'block',
                minWidth: '38px',
                width: '100%',
                height: '40px',
                borderRadius: 0,
                padding: 0,
                backgroundColor: calendarColorStyle.common.default,
                color: calendarColorStyle.primary[600],
                '&:hover, &:focus, &:active, &:visited': {
                    backgroundColor: 'transparent',
                    color: calendarColorStyle.primary[700],
                },
                [theme.breakpoints.down(360)]: {
                    fontSize: '0.8em',
                },
            },
            '&:hover, &:focus, &:active, &:visited': {
                backgroundColor: calendarColorStyle.common.selection,
                border: `1px solid ${calendarColorStyle.primary[600]}`,
                borderRadius: '3px',
                '&$lastDaySelected': {
                    borderLeft: '0',
                    borderTopLeftRadius: '0',
                    borderBottomLeftRadius: '0',
                },
                '&$lastDaySelected$firstDaySelected': {
                    borderLeft: `1px solid ${calendarColorStyle.primary[600]}`,
                    borderTopLeftRadius: '3px',
                    borderBottomLeftRadius: '3px',
                },
            },
        },
        dayDisabled: {
            color: calendarColorStyle.primary[200],
            backgroundColor: calendarColorStyle.common.default,
            borderTop: `1px solid ${calendarColorStyle.common.default}`,
            borderBottom: `1px solid ${calendarColorStyle.common.default}`,
            '& button': {
                [theme.breakpoints.down(360)]: {
                    fontSize: '0.8em',
                },
                backgroundColor: calendarColorStyle.common.default,
                '&:hover, &:focus, &:active, &:visited': {
                    backgroundColor: calendarColorStyle.common.default,
                },
            },
            '&:hover, &:focus, &:active, &:visited': {
                backgroundColor: calendarColorStyle.common.default,
                border: '1px solid transparent',
            },
        },
        dayValue: {
            display: 'block',
        },
        pricePerDay: {
            display: 'block',
            fontSize: '0.715em',
            textTransform: 'none',
        },
        bannerHoliday: {
            backgroundColor: calendarColorStyle.primary[100],
            color: calendarColorStyle.primary[600],
            border: `1px solid ${calendarColorStyle.primary[100]}`,
            '& button': {
                backgroundColor: calendarColorStyle.primary[100],
                color: calendarColorStyle.primary[600],
            },
        },
        selectedDay: {
            display: 'block',
            backgroundColor: calendarColorStyle.common.selection,
            borderTop: `1px solid ${calendarColorStyle.primary[600]}`,
            borderBottom: `1px solid ${calendarColorStyle.primary[600]}`,
            borderLeft: 0,
            borderRight: 0,
            '& button': {
                fontSize: '0.875em',
                lineHeight: '1em',
                display: 'block',
                minWidth: '38px',
                width: '100%',
                height: '40px',
                borderRadius: 0,
                padding: 0,
                backgroundColor: calendarColorStyle.common.selection,
                color: calendarColorStyle.primary[700],
                [theme.breakpoints.down(360)]: {
                    fontSize: '0.8em',
                },
            },
            '&$dayDisabled': {
                backgroundColor: calendarColorStyle.common?.selection,
                borderTop: `1px solid ${calendarColorStyle.primary[600]}`,
                borderBottom: `1px solid ${calendarColorStyle.primary[600]}`,
                '& button': {
                    backgroundColor: calendarColorStyle.common.selection,
                    color: calendarColorStyle.primary[700],
                },
                '&:hover, &:focus, &:active, &:visited': {
                    backgroundColor: calendarColorStyle.common.default,
                    border: 0,
                    borderTop: `1px solid ${calendarColorStyle.primary[600]}`,
                    borderBottom: `1px solid ${calendarColorStyle.primary[600]}`,
                    borderRadius: 0,
                },
            },
        },
        firstDaySelected: {
            borderLeft: `1px solid ${calendarColorStyle.primary[600]}`,
            borderTopLeftRadius: '3px',
            borderBottomLeftRadius: '3px',
            '& button': {
                borderTopLeftRadius: '3px',
                borderBottomLeftRadius: '3px',
            },
        },
        lastDaySelected: {
            borderRight: `1px solid ${calendarColorStyle.primary[600]}`,
            borderTopRightRadius: '3px',
            borderBottomRightRadius: '3px',
            '& button': {
                borderTopRightRadius: '3px',
                borderBottomRightRadius: '3px',
            },
        },
        holidayIcon: {
            position: 'absolute',
            top: '4px',
            right: '4px',
            width: '10px',
            color: calendarColorStyle.primary.main,
        },
    }),
    { name: 'MonthDaysCalendar' },

);

const MonthDaysCalendar = ({
    bannerData,
    calendarData,
    calendarListByDates,
    cmsData,
    isHolidayBannerEnabled,
    handleSetSelectedDateDetails,
    holidayDateCollection,
    orderItem,
    selectedDateRange,
    setForceExactDay,
    setHolidaySelected,
    setSelectedHoliday,
    startDateCalendar,
}) => {
    const classes = useStyles();
    const dispatchAction = useDispatch();
    const isPassportUser = useSelector(getIsPassportUserLoggedIn);
    const passportSubscriptionStatus = useSelector(getPassportSubscriptionStatus);
    const shopRunnerLoggedIn = useSelector(getShopRunnerToken);

    const dateIcons = {};
    const calenderDateIcon = cmsData?.entries?.[0]?.calendar_date_icons || [];

    const dateIconArray = calenderDateIcon?.[0]?.date && calenderDateIcon;
    if (!(['1', '2'].includes(orderItem.product.sympathyValue)) && dateIconArray && dateIconArray.length > 0) {
        dateIconArray.forEach((dateInfo) => {
            dateIcons[dateInfo.date] = dateInfo;
        });
    }

    const getDayOfTheWeekHeader = () => ['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, indexDay) => (
        <span className={classes.weekContainer} key={uid(day, indexDay)}>
            {day}
        </span>
    ));

    const handleSelectedDate = (date) => {
        const selDate = convertDateObjToStrDate(date);
        const selDateProps = calendarListByDates[selDate];

        if (selDateProps) {
            setForceExactDay(false);
            setHolidaySelected(selDateProps[0]?.holidayCode?.[0] || 'Holiday Delivery');
            if (!selDateProps[0]?.holidayCode?.[0]) {
                setSelectedHoliday({});
            }
            handleSetSelectedDateDetails(selDateProps, date, true);
            if (selDateProps[0]?.holidayCode?.[0]) {
                dispatchAction(
                    trackEvent({
                        eventName: 'feature_interaction',
                        eventCategory: 'Checkout',
                        eventAction: 'Calendar',
                        eventLabel: 'Holiday Date Selected',
                    }),
                );
            }
        }
    };

    const showDay = (day, index) => {
        if (day) {
            const dateYYYYMMDD = convertDateObjToStrDate(day);
            const today = new Date();
            const startWeekToday = startOfWeek(today);
            const startWeekTodayYYYYMMDD = convertDateObjToStrDate(startWeekToday);

            const keyDay = `dayIndex${index}_${dateYYYYMMDD}`;
            const dateDetails = calendarListByDates[dateYYYYMMDD];

            const upchargeStyle = classes.pricePerDay;
            let isEnabledBtn = false;
            let firstDaySelected = ''; // first button selected style
            let lastDaySelected = ''; // last button selected style
            let upCharge = '';
            let isHoliday = (
                dateDetails
                && (
                    holidayDateCollection.indexOf(dateYYYYMMDD) >= 0
                    || dateDetails[0]?.holidayCode?.[0]
                )
            );

            if (dateDetails) {
                isEnabledBtn = true;
                const totalCharges = (parseFloat(dateDetails[0].upcharge || 0, 10)) + (parseFloat(dateDetails[0].surcharge || 0, 10));
                if (totalCharges > 0) {
                    upCharge = `+$${totalCharges.toFixed(2)}`;
                }
            }

            // Free Shipping
            if (!upCharge && (isPassportUser || passportSubscriptionStatus || shopRunnerLoggedIn)) {
                upCharge = '';
            }

            const rootClasses = {
                root: classes.dayContainer,
                disable: `${classes.dayContainer} ${classes.dayDisabled}`,
            };

            let isDateForHolidayBanner = false;
            if (
                isHolidayBannerEnabled
                && bannerData?.flexBannerHoliday?.holiday_date_range?.start_date
                && bannerData?.flexBannerHoliday?.holiday_date_range?.end_date
            ) {
                const holidayRangeDate = bannerData.flexBannerHoliday.holiday_date_range;
                // // This conditions is exclusive for testing DELETE AFTER FINISH ++++++++++++++++++++++++++++++++++++++++++
                // if (holidayRangeDate) {
                //     holidayRangeDate = {
                //         start_date: '2025-11-24',
                //         end_date: '2025-11-27',
                //     };
                // }
                // // end
                const holidayBannerStartDate = convertDateStringToObj(holidayRangeDate.start_date);
                const holidayBannerEndDate = convertDateStringToObj(holidayRangeDate.end_date);
                if (
                    day >= holidayBannerStartDate
                    && day <= holidayBannerEndDate
                ) {
                    isDateForHolidayBanner = true;
                }
            }

            let markAsSelected = '';
            if (
                selectedDateRange.includes(dateYYYYMMDD)
                && day >= startWeekToday
            ) {
                markAsSelected = classes.selectedDay;
            }

            // Show the surchage value only in the last position of the range
            // if it have any value
            if (
                markAsSelected
                && selectedDateRange.length > 1
                && selectedDateRange.indexOf(dateYYYYMMDD) >= 0
            ) {
                upCharge = '';
            }

            if (
                selectedDateRange?.length > 0
                && (
                    selectedDateRange.indexOf(dateYYYYMMDD) === 0
                    || (
                        dateYYYYMMDD === startWeekTodayYYYYMMDD
                        && selectedDateRange.includes(startWeekTodayYYYYMMDD)
                    )
                )
            ) {
                firstDaySelected = classes.firstDaySelected;
            }

            if (selectedDateRange?.length > 0 && selectedDateRange.indexOf(dateYYYYMMDD) === (selectedDateRange.length - 1)  && calendarListByDates[dateYYYYMMDD]) {
                lastDaySelected = classes.lastDaySelected;
            }

            const dayDate = day.getDate();
            let dataIconUrl = 'https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/bltecf9432c6017abda/68e52e803e81f704bc943f7e/enterprise-calendar-food-holiday-icon.svg';

            if (dateIcons[dateYYYYMMDD]?.icon && dateDetails) {
                isHoliday = true;
                dataIconUrl = dateIcons[dateYYYYMMDD].icon.url;
            }

            return (
                <div key={keyDay} data-test={keyDay} className={`${markAsSelected} ${(isEnabledBtn ? `button-enabled ${rootClasses?.root}` : `button-disabled ${rootClasses?.disable}`)} ${firstDaySelected} ${lastDaySelected} ${isDateForHolidayBanner ? classes.bannerHoliday : ''}`}>
                    <Button disabled={!isEnabledBtn} onClick={() => { handleSelectedDate(day); }} className={!isEnabledBtn ? classes.disabledBtn : ''}>
                        <span className={classes.dayValue}>
                            {dayDate}
                            {
                                isHoliday
                                && (
                                    <img
                                        className={classes.holidayIcon}
                                        src={dataIconUrl}
                                        alt="*"
                                    />
                                )
                            }
                        </span>
                        {
                            upCharge
                            && (
                                <span className={upchargeStyle}>
                                    <span className={classes.chargeTextStyle}>
                                        {upCharge}
                                    </span>
                                </span>
                            )
                        }
                    </Button>
                </div>
            );
        }

        return (
            <div key={uid('day', index)} className={`${classes.dayContainer} ${classes.dayDisabled}`}>
                <Button disabled />
            </div>
        );
    };

    const renderCalendar = () => {
        const startDateFromCalendarResponse = convertDateStringToObj(calendarData.calendarDates[0].deliveryDate);

        const startDate = convertDateStringToObj(startDateCalendar);
        let startDateStartOfWeek = startOfWeek(startDate);
        let endDateOfMonthWeek = null;
        let currentMonth = startDate.getMonth();

        // set with the first date from calendar response the end of the week
        const startDateEndOfWeek = endOfWeek(startDateFromCalendarResponse);

        // if the first day from the response is same month as start date and
        // end of the week still is the same month, we show current month
        // calendar from 1 to 30
        if (isSameMonth(startDateEndOfWeek, startDate)) {
            const endOfMonthCalendar = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
            endDateOfMonthWeek = endOfWeek(endOfMonthCalendar);
        } else {
            // start of the week will start with the last week of the previous month
            startDateStartOfWeek = startOfWeek(startDateFromCalendarResponse);
            // if it is not same month mean is the last week of the month
            // we calculate the next month end week
            const endOfMonthCalendar = new Date(startDate.getFullYear(), startDate.getMonth() + 2, 0);
            endDateOfMonthWeek = endOfWeek(endOfMonthCalendar);
            currentMonth = endOfMonthCalendar.getMonth();
        }

        const keyMonth = `calendar-${startDate.getFullYear()}-${startDate.getMonth() + 1}`;

        const rows = [];
        let days = [];
        let day = startDateStartOfWeek;

        while (day <= endDateOfMonthWeek) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < 7; i++) {
                const currentDay = day;
                days.push(showDay(currentDay, i));
                day = addDays(day, 1);
            }
            rows.push(
                <div key={`week-${format(day, 'w')}`} className={classes.daysGrid}>
                    {days}
                </div>,
            );
            days = [];
        }

        return (
            <Grid item xs={12} id={keyMonth} key={keyMonth} className={classes.monthCalendar}>
                <Grid container>
                    <div className={classes.weekdays}>
                        {getDayOfTheWeekHeader()}
                    </div>
                    {rows.flat()}
                </Grid>
            </Grid>
        );
    };

    if (Object.keys(calendarListByDates).length === 0) {
        return null;
    }

    return (
        <Grid
            container
            id="month-and-days-calendar"
            className={classes.calendarContainer}
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
        >
            {renderCalendar()}
        </Grid>
    );
};

MonthDaysCalendar.propTypes = {
    bannerData: object.isRequired,
    calendarData: object.isRequired,
    calendarListByDates: object.isRequired,
    cmsData: object,
    isHolidayBannerEnabled: bool.isRequired,
    handleSetSelectedDateDetails: func.isRequired,
    holidayDateCollection: array.isRequired,
    orderItem: object.isRequired,
    selectedDateRange: array.isRequired,
    setForceExactDay: func.isRequired,
    setHolidaySelected: func.isRequired,
    setSelectedHoliday: func.isRequired,
    startDateCalendar: string.isRequired,
};

MonthDaysCalendar.defaultProps = {
    cmsData: {},
};

export default MonthDaysCalendar;
